import { Map as ImmutableMap } from 'immutable';

import { toI18n } from 'util/i18n';
// Types
type PeriodType = 'monthly' | string;
type ContributionTypeKey = 'employee' | 'company';

interface BenefitTypeOption {
  value: string;
  label: string;
}

type DeductionData = ImmutableMap<string, any>;

// Constants
const CONTRIBUTION_TYPES = {
  AMOUNT: 'amount' as const,
  PERCENT: 'percent' as const,
};

const PERIODS = {
  MONTHLY: 'monthly' as const,
};

const I18N_KEYS = {
  PER_MONTH: 'team.employee_profile.tax_deductions.per_month_text',
  PER_PAY_PERIOD: 'team.employee_profile.tax_deductions.per_pay_period_text',
};

const DEFAULT_VALUE = '--';

/**
 * Gets the appropriate contribution value based on period and type
 */
const getContributionValue = (
  deduction: DeductionData,
  type: ContributionTypeKey,
  period: PeriodType
): string => {
  if (period === PERIODS.MONTHLY) {
    const periodAmount = deduction.get(`${type}_period_amount`);
    const regularContribution = deduction.get(`${type}_contribution`);

    // For monthly period, use period_amount if it exists, otherwise fall back to regular
    return periodAmount || regularContribution || '0';
  }
  return deduction.get(`${type}_contribution`) || '0';
};

/**
 * Determines if the contribution should be treated as an amount based on period and available data
 */
const shouldTreatAsAmount = (
  deduction: DeductionData,
  type: ContributionTypeKey,
  period: PeriodType,
  contributionType: string
): boolean => {
  // If period is monthly and period_amount exists, always treat as amount
  if (period === PERIODS.MONTHLY) {
    const periodAmount = deduction.get(`${type}_period_amount`);
    if (periodAmount) {
      return true;
    }
  }

  // Otherwise, use the contribution_type
  return contributionType === CONTRIBUTION_TYPES.AMOUNT;
};

/**
 * Gets the appropriate period text based on the period type
 */
const getPeriodText = (period: PeriodType | null | undefined): string => {
  const i18nKey =
    period === PERIODS.MONTHLY ? I18N_KEYS.PER_MONTH : I18N_KEYS.PER_PAY_PERIOD;
  return ` ${toI18n(i18nKey)}`;
};

/**
 * Formats the final value with currency/percentage symbol and period text
 */
const formatValue = (
  contribution: string,
  isAmount: boolean,
  periodText: string
): string =>
  isAmount ? `$${contribution}${periodText}` : `${contribution}%${periodText}`;

/**
 * Formats a contribution value for display based on type and period
 */
export const formatContributionValue = (
  deduction: DeductionData,
  type: ContributionTypeKey
): string => {
  const contributionType = deduction.get(`${type}_contribution_type`);
  if (!contributionType) {
    return DEFAULT_VALUE;
  }

  const period = deduction.get('period');
  const contribution = getContributionValue(deduction, type, period || '');
  const isAmount = shouldTreatAsAmount(
    deduction,
    type,
    period || '',
    contributionType
  );
  const periodText = getPeriodText(period);

  return formatValue(contribution, isAmount, periodText);
};

/**
 * Finds a benefit type option by matching the benefit's type value
 */
export const getBenefitType = (
  benefit: DeductionData | null | undefined,
  benefitTypeOptions: BenefitTypeOption[] | null | undefined
): BenefitTypeOption | undefined => {
  if (!benefit || !benefitTypeOptions || !Array.isArray(benefitTypeOptions)) {
    return undefined;
  }

  const benefitType = benefit.get('benefit_type');
  return benefitTypeOptions.find(option => option.value === benefitType);
};
